/**
 * Encuadrar la foto antes de subirla.
 *
 * ── Los dos problemas que resuelve ──────────────────────────────────────────
 *
 * 1. **El encuadre lo decidía el CSS.** Todo avatar se muestra con
 *    `object-fit: cover`, o sea recortado al centro geométrico del archivo. Una
 *    foto donde la persona está a un lado, o un retrato vertical donde la
 *    cabeza está arriba, salían mal y no había forma de arreglarlo.
 * 2. **El límite de 4 MB solo sabía decir que no.** El archivo se guardaba tal
 *    cual en R2, así que una foto de 3,9 MB entraba entera y una de 4,1 MB no
 *    se podía subir. Lo peor de los dos mundos: no ahorraba nada y bloqueaba.
 *
 * Los dos se resuelven en el mismo lugar. Lo que sale de acá es un JPEG
 * cuadrado de 512 px como mucho —entre 40 y 70 KB— ya encuadrado a mano. El
 * tope del servidor deja de alcanzarse solo, y de paso R2 guarda dos órdenes
 * de magnitud menos.
 *
 * ── Por qué un `<canvas>` y no un `<img>` ───────────────────────────────────
 *
 * La CSP de la app es `img-src 'self' data:` (ver `apps/worker/src/index.ts`).
 * Un `<img src={URL.createObjectURL(archivo)}>` —que es como se hace esto en
 * todos lados— queda BLOQUEADO en producción y funciona perfecto en `vite dev`,
 * donde no hay CSP: o sea que pasaría cualquier revisión y fallaría recién en
 * cleanblue.cc. El lienzo no toca `img-src`.
 *
 * ── Un solo bitmap, no dos ──────────────────────────────────────────────────
 *
 * Se decodifica una vez y de ahí salen el editor, las previas y el recorte
 * final. Decodificar aparte para ver y para guardar significa dos
 * decodificadores decidiendo la orientación EXIF por separado: si difieren, lo
 * que se encuadró no es lo que se guarda, y no hay manera de darse cuenta.
 *
 * La matemática del encuadre vive en `lib/imagen.ts`, que es la única parte de
 * esto que se puede probar sin un navegador.
 */

import { useEffect, useRef, useState } from 'react';
import { haptic } from '@/lib/haptics';
import { Button, Note } from '../ui';
import {
  acotar, CALIDAD_JPEG, conEscala, encuadreInicial, ESCALA_MAX, ladoSalida,
  leerCabeceraJpeg, limites, regionFuente, rotacionPendiente,
  TOPE_DECODIFICACION, type Encuadre, type Forma,
} from '@/lib/imagen';

/** Los dos tamaños a los que de verdad se ve un avatar en la app. */
const PREVIAS = [
  { lado: 28, rotulo: 'en las listas' },
  { lado: 64, rotulo: 'en la ficha' },
];

export function RecorteFoto({ archivo, forma = 'circulo', subiendo, error, onListo, onCancelar }: {
  archivo: File;
  /** Redonda para personas, cuadrada para un producto. Solo cambia la máscara
   *  y las previas: lo que se guarda es un cuadrado en los dos casos, porque la
   *  redondez la sigue poniendo `.avatar-img` por CSS. */
  forma?: Forma;
  /** La subida la maneja `PhotoUploader`, no esto — así el encuadre sigue en
   *  pantalla si el POST falla, en vez de mandar a buscar el archivo de nuevo. */
  subiendo: boolean;
  error?: string;
  onListo: (recorte: File) => void;
  onCancelar: () => void;
}) {
  const lienzo = useRef<HTMLCanvasElement>(null);
  const previas = useRef<(HTMLCanvasElement | null)[]>([]);
  const bitmap = useRef<ImageBitmap | null>(null);
  const dedos = useRef(new Map<number, { x: number; y: number }>());
  const arrastre = useRef<{ x: number; y: number; e: Encuadre } | null>(null);
  const pellizco = useRef<{ dist: number; escala: number } | null>(null);

  const [medidas, setMedidas] = useState<{ w: number; h: number } | null>(null);
  const [encuadre, setEncuadre] = useState<Encuadre>({ escala: 1, dx: 0, dy: 0 });
  const [fallo, setFallo] = useState<string | null>(null);
  const [preparando, setPreparando] = useState(false);

  /* ── Decodificar ───────────────────────────────────────────────────────── */

  useEffect(() => {
    let vivo = true;
    let propio: ImageBitmap | null = null;

    (async () => {
      try {
        /* La cabecera primero, con 128 KB: hay que saber cuánto topear ANTES de
           decodificar. Decodificar para después decidir el tope asigna
           justamente los 48 MB de RGBA que el tope existe para evitar, y el
           teléfono se muere ahí, no en la segunda pasada. */
        const cab = leerCabeceraJpeg(new Uint8Array(await archivo.slice(0, 128 * 1024).arrayBuffer()));
        const mayor = cab ? Math.max(cab.ancho, cab.alto) : 0;
        const k = mayor > TOPE_DECODIFICACION ? TOPE_DECODIFICACION / mayor : 1;

        let bmp = await createImageBitmap(archivo, {
          imageOrientation: 'from-image',
          // El default es 'low' y el aliasing se ve a simple vista al achicar
          // una foto de 4000 px a 320.
          resizeQuality: 'high',
          ...(cab && k < 1
            ? { resizeWidth: Math.round(cab.ancho * k), resizeHeight: Math.round(cab.alto * k) }
            : {}),
        });

        /* Y ahora se VERIFICA que la orientación se haya aplicado, en vez de
           confiar en que un `catch` avise: `imageOrientation` es un miembro de
           diccionario, así que un navegador que no lo conoce lo ignora en
           silencio y devuelve la foto acostada. No tira nada. Sin este chequeo,
           la foto vertical del iPhone sale de lado y no hay ningún error en
           ninguna parte que lo explique. */
        const grados = cab ? rotacionPendiente(cab, bmp.width, bmp.height) : 0;
        if (grados !== 0) bmp = await rotar(bmp, grados);

        if (!vivo) { bmp.close(); return; }
        propio = bmp;
        bitmap.current = bmp;
        setMedidas({ w: bmp.width, h: bmp.height });
        setEncuadre(encuadreInicial(bmp.width, bmp.height, forma));
      } catch {
        /* HEIC en Windows es el caso real: el iPhone entrega JPEG por el
           selector, pero un HEIC copiado a la computadora no lo abre ni Chrome
           ni Firefox. El mensaje dice qué hacer, que es lo único útil acá. */
        if (vivo) setFallo('No pudimos abrir esta imagen. Probá convertirla a JPG y subirla de nuevo.');
      }
    })();

    return () => {
      vivo = false;
      // Una foto de 12 MP son decenas de MB retenidos por cada vez que se abre
      // el editor. Sin esto, cinco fotos seguidas tumban el navegador.
      propio?.close();
      bitmap.current = null;
    };
  }, [archivo, forma]);

  /* ── Pintar ────────────────────────────────────────────────────────────── */

  useEffect(() => {
    const cv = lienzo.current;
    const bmp = bitmap.current;
    if (!cv || !bmp || !medidas) return;

    const pintar = () => {
      const caja = cv.getBoundingClientRect();
      /* El lado más corto y no el ancho: el CSS ya se encarga de que el lienzo
         sea cuadrado, pero si alguna vez deja de serlo, dibujar sobre el ancho
         estira el círculo en una elipse — y la ventana pasa a mentir sobre lo
         que se va a recortar, que es el único trabajo que tiene. */
      const lado = Math.min(caja.width, caja.height);
      if (lado < 1) return;
      const dpr = window.devicePixelRatio || 1;
      // Asignar width/height BORRA el lienzo, así que solo se toca cuando de
      // verdad cambió: si no, cada repintado parpadea.
      const px = Math.round(lado * dpr);
      if (cv.width !== px || cv.height !== px) { cv.width = px; cv.height = px; }

      const ctx = cv.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, lado, lado);

      const { sx, sy, sLado } = regionFuente(encuadre, medidas.w, medidas.h);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Lo de afuera de la ventana se ve, atenuado: sirve para saber qué hay
      // alrededor mientras se arrastra. Lo de adentro, a plena luz.
      const sobra = 2;   // cuántas ventanas de contexto se muestran alrededor
      ctx.globalAlpha = 0.28;
      ctx.drawImage(
        bmp,
        sx - sLado * sobra, sy - sLado * sobra, sLado * (1 + sobra * 2), sLado * (1 + sobra * 2),
        -lado * sobra, -lado * sobra, lado * (1 + sobra * 2), lado * (1 + sobra * 2),
      );
      ctx.globalAlpha = 1;

      ctx.save();
      ctx.beginPath();
      if (forma === 'circulo') ctx.arc(lado / 2, lado / 2, lado / 2, 0, Math.PI * 2);
      else ctx.rect(0, 0, lado, lado);
      ctx.clip();
      ctx.drawImage(bmp, sx, sy, sLado, sLado, 0, 0, lado, lado);
      ctx.restore();

      // El borde de la ventana, para que se vea dónde termina el recorte.
      ctx.strokeStyle = 'rgba(255,255,255,.9)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      if (forma === 'circulo') ctx.arc(lado / 2, lado / 2, lado / 2 - 1, 0, Math.PI * 2);
      else ctx.rect(1, 1, lado - 2, lado - 2);
      ctx.stroke();

      // Las previas, al tamaño real al que se va a ver el avatar.
      for (const cvp of previas.current) {
        if (!cvp) continue;
        const ladoP = Math.round(cvp.clientWidth * dpr);
        if (ladoP < 1) continue;
        if (cvp.width !== ladoP || cvp.height !== ladoP) { cvp.width = ladoP; cvp.height = ladoP; }
        const cp = cvp.getContext('2d');
        if (!cp) continue;
        cp.clearRect(0, 0, ladoP, ladoP);
        cp.imageSmoothingEnabled = true;
        cp.imageSmoothingQuality = 'high';
        cp.drawImage(bmp, sx, sy, sLado, sLado, 0, 0, ladoP, ladoP);
      }
    };

    pintar();
    const ro = new ResizeObserver(pintar);
    ro.observe(cv);
    return () => ro.disconnect();
  }, [encuadre, medidas, forma]);

  /* ── La rueda y el pellizco del trackpad ───────────────────────────────── */

  useEffect(() => {
    const cv = lienzo.current;
    if (!cv || !medidas) return;

    /* Listener nativo con `{ passive: false }`, no `onWheel` de JSX: React
       registra `wheel` como pasivo en la raíz, así que un `preventDefault`
       adentro del handler de JSX no hace nada. El síntoma no es que falle el
       zoom — es que el zoom funciona Y ADEMÁS el cuerpo del modal scrollea. */
    const alRodar = (ev: WheelEvent) => {
      ev.preventDefault();
      const caja = cv.getBoundingClientRect();
      const lado = Math.min(caja.width, caja.height) || 1;
      const ancla = {
        x: (ev.clientX - caja.left) / lado - 0.5,
        y: (ev.clientY - caja.top) / lado - 0.5,
      };
      // El pellizco del trackpad llega como `wheel` con `ctrlKey` y deltas de
      // 1 a 10; una rueda de mouse manda ~100. Dos constantes, no una.
      const paso = Math.exp(-ev.deltaY * (ev.ctrlKey ? 0.01 : 0.0015));
      setEncuadre((e) => conEscala(e, e.escala * paso, ancla, medidas.w, medidas.h));
    };

    // Propietarios de Safari, sin equivalente en React: sin esto, el pellizco
    // sobre el editor hace zoom de la PÁGINA entera.
    const frenar = (ev: Event) => ev.preventDefault();

    cv.addEventListener('wheel', alRodar, { passive: false });
    cv.addEventListener('gesturestart', frenar);
    cv.addEventListener('gesturechange', frenar);
    return () => {
      cv.removeEventListener('wheel', alRodar);
      cv.removeEventListener('gesturestart', frenar);
      cv.removeEventListener('gesturechange', frenar);
    };
  }, [medidas]);

  /* ── Arrastre y pellizco ───────────────────────────────────────────────── */

  function unidades(dxPx: number, dyPx: number): { x: number; y: number } {
    const caja = lienzo.current?.getBoundingClientRect();
    const lado = (caja ? Math.min(caja.width, caja.height) : 0) || 1;
    return { x: dxPx / lado, y: dyPx / lado };
  }

  function alBajar(ev: React.PointerEvent<HTMLCanvasElement>) {
    ev.currentTarget.setPointerCapture(ev.pointerId);
    dedos.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    if (dedos.current.size === 1) {
      arrastre.current = { x: ev.clientX, y: ev.clientY, e: encuadre };
      pellizco.current = null;
    } else if (dedos.current.size === 2) {
      arrastre.current = null;
      pellizco.current = { dist: separacion(), escala: encuadre.escala };
    }
  }

  function alMover(ev: React.PointerEvent<HTMLCanvasElement>) {
    if (!dedos.current.has(ev.pointerId) || !medidas) return;
    dedos.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (pellizco.current && dedos.current.size >= 2) {
      const d = separacion();
      if (d > 0 && pellizco.current.dist > 0) {
        const nueva = pellizco.current.escala * (d / pellizco.current.dist);
        setEncuadre((e) => conEscala(e, nueva, null, medidas.w, medidas.h));
      }
      return;
    }
    const a = arrastre.current;
    if (!a) return;
    const d = unidades(ev.clientX - a.x, ev.clientY - a.y);
    setEncuadre(acotar({ escala: a.e.escala, dx: a.e.dx + d.x, dy: a.e.dy + d.y }, medidas.w, medidas.h));
  }

  /* `pointercancel` y `lostpointercapture` limpian igual que `pointerup`: sin
     eso, una llamada entrante o un gesto que el sistema se lleva dejan el dedo
     pegado, y el siguiente toque hace saltar la foto por toda la diferencia
     acumulada mientras tanto. */
  function alSoltar(ev: React.PointerEvent<HTMLCanvasElement>) {
    dedos.current.delete(ev.pointerId);
    if (dedos.current.size < 2) pellizco.current = null;
    if (dedos.current.size === 0) arrastre.current = null;
    else {
      const [primero] = [...dedos.current.values()];
      if (primero) arrastre.current = { x: primero.x, y: primero.y, e: encuadre };
    }
  }

  function separacion(): number {
    const [a, b] = [...dedos.current.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  /* ── Teclado ───────────────────────────────────────────────────────────── */

  function alTeclear(ev: React.KeyboardEvent<HTMLCanvasElement>) {
    if (!medidas) return;
    const paso = ev.shiftKey ? 0.16 : 0.04;
    const mover = (x: number, y: number) => {
      ev.preventDefault();   // si no, las flechas mueven la foto Y scrollean
      setEncuadre((e) => acotar({ ...e, dx: e.dx + x, dy: e.dy + y }, medidas.w, medidas.h));
    };
    switch (ev.key) {
      case 'ArrowLeft': return mover(paso, 0);
      case 'ArrowRight': return mover(-paso, 0);
      case 'ArrowUp': return mover(0, paso);
      case 'ArrowDown': return mover(0, -paso);
      case '+': case '=':
        ev.preventDefault();
        return setEncuadre((e) => conEscala(e, e.escala * 1.15, null, medidas.w, medidas.h));
      case '-': case '_':
        ev.preventDefault();
        return setEncuadre((e) => conEscala(e, e.escala / 1.15, null, medidas.w, medidas.h));
      case 'Home':
        ev.preventDefault();
        return setEncuadre(encuadreInicial(medidas.w, medidas.h, forma));
      default:
    }
  }

  /* ── Guardar ───────────────────────────────────────────────────────────── */

  async function usar() {
    const bmp = bitmap.current;
    if (!bmp || !medidas || preparando || subiendo) return;
    /* `toBlob` tarda decenas de milisegundos y `subiendo` lo enciende el padre
       DESPUÉS, así que entre el clic y el callback no hay nada que frene un
       segundo toque — que es lo normal cuando algo no responde al instante. Dos
       toques serían dos POST, dos escrituras a R2 y una foto huérfana. */
    setPreparando(true);
    try {
      const { sx, sy, sLado } = regionFuente(encuadre, medidas.w, medidas.h);
      const lado = ladoSalida(sLado);
      const salida = document.createElement('canvas');
      salida.width = lado;
      salida.height = lado;
      const ctx = salida.getContext('2d');
      if (!ctx) throw new Error('sin canvas');
      /* Blanco literal, NO `var(--surface)`: el canvas parsea un color de CSS y
         `var()` no se resuelve fuera del árbol de estilos. Al asignar algo
         inválido, `fillStyle` conserva lo que tenía —negro opaco en un contexto
         nuevo—, así que esa línea produciría el defecto que dice arreglar. */
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, lado, lado);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bmp, sx, sy, sLado, sLado, 0, 0, lado, lado);

      const blob = await new Promise<Blob | null>((res) => { salida.toBlob(res, 'image/jpeg', CALIDAD_JPEG); });
      if (!blob) throw new Error('sin blob');
      /* `File` y no `Blob` pelado: los cinco endpoints hacen
         `if (!(file instanceof File)) throw 'Adjuntá una imagen'`, y un Blob
         entra al FormData sin quejarse para fallar recién en el servidor con un
         mensaje que no tiene ninguna relación con lo que la persona hizo. */
      haptic.success();   // HAPTIC_FEEDBACK
      onListo(new File([blob], 'foto.jpg', { type: 'image/jpeg' }));
    } catch {
      setFallo('No pudimos preparar la imagen. Probá con otra foto.');
    } finally {
      setPreparando(false);
    }
  }

  /* ── Pantalla ──────────────────────────────────────────────────────────── */

  if (fallo) {
    return (
      <>
        <Note tone="danger">{fallo}</Note>
        <div className="row" style={{ gap: 8, marginTop: 10 }}>
          <Button onClick={onCancelar}>Cerrar</Button>
        </div>
      </>
    );
  }

  if (!medidas) return <p className="small muted">Abriendo la foto…</p>;

  const { lx, ly } = limites(medidas.w, medidas.h, encuadre.escala);
  const fijo = lx === 0 && ly === 0 && encuadre.escala === 1;

  return (
    <>
      {/* Previas y lienzo van juntos en un marco: en un teléfono acostado el
          marco se acomoda en fila —si no, el pie del modal queda debajo del
          borde de la pantalla y no se alcanza, porque sobre el lienzo el dedo
          no puede scrollear (`touch-action: none`)—. Ver `.recorte__marco`. */}
      <div className="recorte__marco">
      <div className="recorte__previas">
        {PREVIAS.map((p, i) => (
          <div key={p.lado} style={{ textAlign: 'center' }}>
            <span
              className={`recorte__previa recorte__previa--${forma}`}
              style={{ width: p.lado, height: p.lado }}
            >
              <canvas ref={(el) => { previas.current[i] = el; }} aria-hidden />
            </span>
            <div className="small muted" style={{ marginTop: 4 }}>{p.rotulo}</div>
          </div>
        ))}
      </div>

      <canvas
        ref={lienzo}
        className="recorte__lienzo"
        tabIndex={0}
        /* `application` y no `group`: con `group` el lector de pantalla se queda
           en modo lectura y las flechas nunca llegan al handler, o sea que se
           prometerían teclas que no funcionan. */
        role="application"
        aria-label="Encuadre de la foto. Flechas para mover, más y menos para acercar, Inicio para volver al encuadre automático."
        onPointerDown={alBajar}
        onPointerMove={alMover}
        onPointerUp={alSoltar}
        onPointerCancel={alSoltar}
        onLostPointerCapture={alSoltar}
        onKeyDown={alTeclear}
      />
      </div>

      <div className="recorte__zoom">
        <Button
          size="sm"
          aria-label="Alejar"
          onClick={() => setEncuadre((e) => conEscala(e, e.escala / 1.15, null, medidas.w, medidas.h))}
        >
          −
        </Button>
        {/* El slider NO tiene estado propio: su valor sale de `escala` y su
            `onChange` vuelve por `conEscala`. Un control absoluto manejando una
            operación relativa se desincroniza en el primer recorte de `acotar`
            y el pulgar se siente trabado. */}
        <input
          className="zoom-range"
          type="range"
          min={1}
          max={ESCALA_MAX}
          step={0.01}
          value={encuadre.escala}
          onChange={(ev) => setEncuadre((e) => conEscala(e, Number(ev.target.value), null, medidas.w, medidas.h))}
          aria-label="Acercamiento"
          aria-valuetext={`${encuadre.escala.toFixed(1)} veces`}
        />
        <Button
          size="sm"
          aria-label="Acercar"
          onClick={() => setEncuadre((e) => conEscala(e, e.escala * 1.15, null, medidas.w, medidas.h))}
        >
          +
        </Button>
      </div>

      {fijo && (
        <p className="small muted" style={{ marginTop: 6 }}>
          Esta foto ya es cuadrada: acercá para elegir qué parte se ve.
        </p>
      )}

      {error && <Note tone="danger">{error}</Note>}

      <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <Button variant="primary" onClick={() => void usar()} disabled={preparando || subiendo}>
          {subiendo ? 'Subiendo…' : preparando ? 'Preparando…' : 'Usar esta foto'}
        </Button>
        <Button onClick={() => setEncuadre(encuadreInicial(medidas.w, medidas.h, forma))}>
          Volver al automático
        </Button>
        <span className="spacer" />
        <Button onClick={onCancelar} disabled={subiendo}>Cancelar</Button>
      </div>
    </>
  );
}

/** Rota el bitmap cuando el navegador ignoró la orientación EXIF. */
async function rotar(bmp: ImageBitmap, grados: 90 | 180 | 270): Promise<ImageBitmap> {
  const vertical = grados === 90 || grados === 270;
  const cv = document.createElement('canvas');
  cv.width = vertical ? bmp.height : bmp.width;
  cv.height = vertical ? bmp.width : bmp.height;
  const ctx = cv.getContext('2d');
  if (!ctx) return bmp;
  ctx.translate(cv.width / 2, cv.height / 2);
  ctx.rotate((grados * Math.PI) / 180);
  ctx.drawImage(bmp, -bmp.width / 2, -bmp.height / 2);
  bmp.close();
  return createImageBitmap(cv);
}
